import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TabsComponent from '@components/tabs/tabs.tsx';
import { LanguageEnum } from '@models/language.model.ts';
import useSeriesStore from '@modules/entertainment/pages/series/store/series.store.ts';
import { useErrorMessage } from '@hooks/use-error-message.ts';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput
} from '@mui/material';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';
import useLanguageStore from '@store/language-store/language.store.tsx';
import { EpsiodeDialogFormProps } from '@modules/entertainment/models/series.model.ts';
import { validateNotOnlySpaces } from '@utils/trim-form-values-utils.ts';
import { episodeErrorMessages } from '@modules/entertainment/utils/series.utils.ts';
import MediaLibraryComponent from '@components/media-library-component/media-library-component.tsx';
import { FileTypeEnum } from '@models/media-library.model.ts';

const EpisodeDialog: React.FC<EpsiodeDialogFormProps> = ({
  selectedItem,
  onSave
}) => {
  const form = useForm({ mode: 'onTouched' });

  const {
    reset,
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors, isValid }
  } = form;

  const { t } = useTranslation();
  const { languageList } = useLanguageStore((state) => state);
  const { isEpisodeLoading } = useSeriesStore();
  const getErrorMessage = useErrorMessage();

  const defaultLang = languageList[0]?.value || LanguageEnum.English;
  const [currentTab, setCurrentTab] = React.useState<string>(defaultLang);
  const [multiLangFields, setMultiLangFields] = React.useState<{
    title: Record<string, string | undefined>;
    description: Record<string, string | undefined>;
  }>({
    title: {},
    description: {}
  });
  const [selectedMedia, setSelectedMedia] = React.useState<Record<string, any>>(
    {}
  );

  const handleChangeLanguage = (_e: React.SyntheticEvent, newLang: string) => {
    const currentTitle = watch('title');
    const currentDescription = watch('description');

    setMultiLangFields((prev) => ({
      title: { ...prev.title, [currentTab]: currentTitle },
      description: { ...prev.description, [currentTab]: currentDescription }
    }));

    setCurrentTab(newLang);
    setValue('title', multiLangFields.title?.[newLang] || '');
    setValue('description', multiLangFields.description?.[newLang] || '');
  };

  const onSubmit = (data: any) => {
    const currentTitle = watch('title');
    const currentDescription = watch('description');

    const fullData: any = {
      title: { ...multiLangFields.title, [currentTab]: currentTitle },
      description: {
        ...multiLangFields.description,
        [currentTab]: currentDescription
      },
      url: data.url,
      image: selectedMedia.image?.path || null
    };

    onSave(fullData);
  };

  useEffect(() => {
    if (selectedItem) {
      reset({
        title: selectedItem?.title?.[defaultLang] || '',
        description: selectedItem?.description?.[defaultLang] || '',
        url: selectedItem?.url || ''
      });
    }
  }, [defaultLang, selectedItem, reset]);

  return (
    <div>
      <div className="w-full mb-4">
        <TabsComponent
          tabs={languageList}
          value={currentTab}
          onChange={handleChangeLanguage}
        />
      </div>
      <form className="flex flex-col gap-1">
        <div className="w-full flex gap-7">
          <div className="w-full flex flex-col gap-1">
            <FormControl error={!!errors.title}>
              <FormLabel className="flex">
                <span className="reuqired-symbole">*</span>
                {t('label_title')}
                <InfoTooltip text="tooltip_info_movies_title" />
              </FormLabel>
              <OutlinedInput
                placeholder={t('placeholder_episode_title')}
                {...register('title', {
                  required: true,
                  validate: {
                    notOnlySpaces: validateNotOnlySpaces
                  }
                })}
                error={!!errors.title}
              />
              <FormHelperText>
                {errors?.title &&
                  getErrorMessage({
                    errors,
                    fieldName: 'title',
                    errorMessages: episodeErrorMessages
                  })}
              </FormHelperText>
            </FormControl>

            <FormControl error={!!errors.description}>
              <FormLabel className="flex">
                <span className="reuqired-symbole">*</span>
                {t('label_description')}
                <InfoTooltip text="tooltip_info_movies_description" />
              </FormLabel>
              <OutlinedInput
                placeholder={t('placeholder_episode_description')}
                multiline
                rows={4}
                {...register('description', {
                  required: true,
                  validate: {
                    notOnlySpaces: validateNotOnlySpaces
                  }
                })}
                error={!!errors.description}
              />
              <FormHelperText>
                {errors?.description &&
                  getErrorMessage({
                    errors,
                    fieldName: 'description',
                    errorMessages: episodeErrorMessages
                  })}
              </FormHelperText>
            </FormControl>
          </div>

          <div className="w-full flex flex-col gap-1">
            <FormControl error={!!errors.url} className="w-full">
              <FormLabel className="flex">
                <span className="reuqired-symbole">*</span>
                {t('label_url')}
                <InfoTooltip text="tooltip_info_movies_url" />
              </FormLabel>
              <OutlinedInput
                placeholder={t('placeholder_episode_url')}
                rows={4}
                {...register('url', {
                  required: true,
                  validate: {
                    notOnlySpaces: validateNotOnlySpaces
                  }
                })}
                error={!!errors.url}
              />
              <FormHelperText>
                {errors?.url &&
                  getErrorMessage({
                    errors,
                    fieldName: 'url',
                    errorMessages: episodeErrorMessages
                  })}
              </FormHelperText>
            </FormControl>

            <div className="flex gap-4">
              <FormControl>
                <FormLabel className="flex">
                  <span className="reuqired-symbole">*</span>
                  {t('label_poster')}
                  <InfoTooltip text="tooltip_info_movies_poster" />
                </FormLabel>
                <MediaLibraryComponent
                  width={240}
                  height={120}
                  mediaKey={'image'}
                  allowedTypes={[FileTypeEnum.Image]}
                  path={selectedItem?.image}
                  onSelect={(media) =>
                    setSelectedMedia((prev: any) => ({
                      ...prev,
                      image: media
                    }))
                  }
                />
              </FormControl>
            </div>
          </div>
        </div>

        <div className="flex justify-end items-center gap-6">
          <Button
            disabled={!isValid || !selectedMedia.image}
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            loading={isEpisodeLoading()}
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EpisodeDialog;
